import { generatePath } from '@/services/router/generate-path';
import { useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Icon,
  Label,
  Button,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  Spinner,
  PanelActions,
  Badge,
  Link,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type {
  ServicesApiEnvHealthResponse,
  ServicesApiEnvHealthSegmentStats,
  ServicesApiEnvHealthCampaignsResponse,
} from '@/services/api/services-api';
import {
  delayStatus,
  useEnvHealth,
  useEnvHealthCampaigns,
  useEnvHealthSegments,
  useWorkspaceHealthSimulation,
} from '@/features/workspace-health';
import { useAnalytics } from '@/services/analytics';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';

type Impact = 'High' | 'Medium' | 'Low';

type RecommendationAlertAction =
  | { kind: 'link'; label: string; href: string; target?: React.HTMLAttributeAnchorTarget }
  | { kind: 'button'; label: string; icon?: IconName; onClick: () => void };

type Recommendation = {
  id: string;
  title: string;
  description: string;
  impact: Impact;
  actions?: RecommendationAlertAction[];
};

function findCategory(data: ServicesApiEnvHealthResponse, name: string) {
  return data.processing.categories.find((c) => c.name === name) ?? data.egress.categories.find((c) => c.name === name);
}

function isBehind(data: ServicesApiEnvHealthResponse, name: string): boolean {
  const cat = findCategory(data, name);
  if (cat == null) return false;
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color !== 'green';
}

function isBackedUp(data: ServicesApiEnvHealthResponse, name: string): boolean {
  const cat = findCategory(data, name);
  if (cat == null) return false;
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color === 'red';
}

function deriveRecommendations({
  data,
  segments,
  campaigns,
  workspaceId,
  session,
  aiOptimizeEnabled,
}: {
  data: ServicesApiEnvHealthResponse;
  segments: ServicesApiEnvHealthSegmentStats | undefined;
  campaigns: ServicesApiEnvHealthCampaignsResponse | undefined;
  workspaceId: number;
  session: ReturnType<typeof useCopilotSessionContext>;
  aiOptimizeEnabled: boolean;
}): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // 1. Long-running task — one recommendation per priority queue with queued tasks
  const seenPriorities = new Set<string>();
  for (const task of data.tasks.running_tasks ?? []) {
    if (seenPriorities.has(task.priority)) continue;
    const queuedInPriority = data.tasks.queued_by_priority?.[task.priority] ?? 0;
    if (queuedInPriority > 0) {
      seenPriorities.add(task.priority);
      recommendations.push({
        id: `deprioritize_task_${task.priority}`,
        title: 'Long-running task in progress',
        description:
          'A data task is running and other queued tasks will start after it finishes. If needed, you can deprioritize it to let other tasks run first.',
        impact: 'High',
        actions: [
          {
            kind: 'link',
            label: 'View tasks',
            href: generatePath(
              '/workspaces/:workspaceId/health',
              { workspaceId },
              { tab: 'data', expand: task.id, state: task.state },
            ),
          },
        ],
      });
    }
  }

  // 2. Unused segments — suggest archiving
  if (segments != null && segments.unused_count > 0) {
    recommendations.push({
      id: 'archive_unused_segments',
      title: `Archive ${segments.unused_count.toLocaleString()} unused segment${segments.unused_count > 1 ? 's' : ''}`,
      description:
        'These segments are being recalculated on every profile change but are not used in any campaign, broadcast, or journey. Archiving them reduces unnecessary processing.',
      impact: segments.unused_count > 50 ? 'High' : 'Medium',
      actions: aiOptimizeEnabled
        ? [
            {
              kind: 'button' as const,
              label: 'Optimize with agent',
              icon: 'sparkle' as const,
              onClick: () => {
                const newSession = session.reset();
                openSidebar('copilot');
                void newSession.sendMessage(`Archive the ${segments.unused_count} unused segments in this workspace.`);
              },
            },
            {
              kind: 'link' as const,
              label: 'View',
              href: generatePath(
                '/workspaces/:workspaceId/journeys/segments',
                { workspaceId },
                { usage: 'not_in_use' },
              ),
              target: '_blank' as const,
            },
          ]
        : [
            {
              kind: 'link' as const,
              label: 'View unused segments',
              href: generatePath(
                '/workspaces/:workspaceId/journeys/segments',
                { workspaceId },
                { usage: 'not_in_use' },
              ),
              target: '_blank' as const,
            },
          ],
    });
  }

  // 3. Newsletter-only segments — suggest reviewing for cleanup
  if (segments != null && segments.newsletter_only_count > 0) {
    recommendations.push({
      id: 'archive_newsletter_segments',
      title: `Review ${segments.newsletter_only_count.toLocaleString()} newsletter-only segment${
        segments.newsletter_only_count > 1 ? 's' : ''
      }`,
      description:
        'These segments are only used for sent newsletters. Some may be one-time sends safe to archive, while others may be reused for future newsletters. The agent will review them and suggest candidates.',
      impact: segments.newsletter_only_count > 50 ? 'High' : 'Medium',
      actions: aiOptimizeEnabled
        ? [
            {
              kind: 'button' as const,
              label: 'Optimize with agent',
              icon: 'sparkle' as const,
              onClick: () => {
                const newSession = session.reset();
                openSidebar('copilot');
                void newSession.sendMessage(
                  `Review the ${segments.newsletter_only_count} newsletter-only segments in this workspace and suggest which ones are safe to archive. Show me 10 at a time, explain why each one looks like a cleanup candidate, and let me select which ones to archive.`,
                );
              },
            },
            {
              kind: 'link' as const,
              label: 'View',
              href: generatePath(
                '/workspaces/:workspaceId/journeys/segments',
                { workspaceId },
                { usage: 'in_sent_newsletters' },
              ),
              target: '_blank' as const,
            },
          ]
        : [
            {
              kind: 'link' as const,
              label: 'View newsletter-only segments',
              href: generatePath(
                '/workspaces/:workspaceId/journeys/segments',
                { workspaceId },
                { usage: 'in_sent_newsletters' },
              ),
              target: '_blank' as const,
            },
          ],
    });
  }

  // 4. CSV imports or integrations backlogging — review imports
  if (isBackedUp(data, 'csv_imports') || isBackedUp(data, 'integrations')) {
    recommendations.push({
      id: 'review_data_imports',
      title: 'Review active data imports',
      description:
        'Large CSV imports and frequent integration syncs can slow down processing across your workspace. Keep this in mind when scheduling bulk imports.',
      impact: 'Medium',
      actions: [
        {
          label: 'View imports',
          kind: 'link',
          href: generatePath('/workspaces/:workspaceId/journeys/imports', { workspaceId }),
          target: '_blank',
        },
      ],
    });
  }

  // 5. Identify or events severely behind — consider throttling
  if (isBackedUp(data, 'identify') || isBackedUp(data, 'events')) {
    recommendations.push({
      id: 'throttle_incoming_data',
      title: 'Consider throttling incoming data',
      description:
        'Incoming profile updates or events are backed up. This can happen during traffic spikes. If you control the data source, consider reducing the send rate temporarily.',
      impact: 'Medium',
    });
  }

  // 6. Segments processing behind — review segment complexity
  if (isBehind(data, 'segments')) {
    recommendations.push({
      id: 'review_segment_complexity',
      title: 'Review segment complexity',
      description:
        'Segment membership updates are falling behind. This includes static segment changes and scheduled re-evaluations. Complex segments with many conditions or large audiences take longer to process. Simplifying or archiving unused segments can help.',
      impact: 'Medium',
      actions: [
        {
          label: 'View segments',
          kind: 'link',
          href: generatePath('/workspaces/:workspaceId/journeys/segments', { workspaceId }),
          target: '_blank',
        },
      ],
    });
  }

  // 7. Related updates backed up — review object-triggered campaigns
  if (isBehind(data, 'related_updates')) {
    recommendations.push({
      id: 'review_object_triggered_campaigns',
      title: 'Review object-triggered campaigns',
      description:
        'Object and relationship changes trigger downstream processing that shares capacity with message delivery. Reducing unused object-triggered campaigns can free up resources.',
      impact: isBackedUp(data, 'related_updates') ? 'High' : 'Medium',
      actions: [
        {
          label: 'View object-triggered campaigns',
          kind: 'link',
          href: generatePath('/workspaces/:workspaceId/journeys/campaigns', { workspaceId }, { type: 'object' }),
          target: '_blank',
        },
      ],
    });
  }

  // 8. Webhooks backed up — review webhook endpoints
  if (isBackedUp(data, 'webhooks')) {
    recommendations.push({
      id: 'review_webhook_endpoints',
      title: 'Review webhook endpoints',
      description:
        'Outbound webhooks are backed up. This is often caused by slow or unresponsive receiving endpoints. Check that your webhook URLs are responding quickly.',
      impact: 'Medium',
    });
  }

  // 9. Stale campaigns — review campaigns with no recent sends
  if (campaigns != null && campaigns.stale_count > 0) {
    recommendations.push({
      id: 'review_stale_campaigns',
      title: `Review ${campaigns.stale_count} campaign${campaigns.stale_count > 1 ? 's' : ''} with no recent sends`,
      description:
        'These campaigns are active but have not sent any messages in over 30 days. Stopping or archiving them frees up processing capacity.',
      impact: campaigns.stale_count > 5 ? 'High' : 'Medium',
      actions: [
        {
          label: 'View campaigns',
          kind: 'link',
          href: generatePath(
            '/workspaces/:workspaceId/health',
            { workspaceId },
            { tab: 'messaging', drawer: 'campaigns' },
          ),
        },
      ],
    });
  }

  const priority: Record<Impact, number> = { High: 0, Medium: 1, Low: 2 };
  return recommendations.sort((a, b) => priority[a.impact] - priority[b.impact]).slice(0, 3);
}

function RecommendationContent({ recommendation }: { recommendation: Recommendation }) {
  const { track } = useAnalytics();

  const handleActionClick = () => {
    void track('workspaceHealth', 'recommendationClicked', { Recommendation: recommendation.id });
  };

  return (
    <Stack gap="200">
      <Stack gap="100">
        <Text size="s" weight="semibold">
          {recommendation.title}
        </Text>
        <Text size="xs" color="subtle">
          {recommendation.description}
        </Text>
        <Box>
          <Label color="outline">{recommendation.impact} impact</Label>
        </Box>
      </Stack>
      {recommendation.actions != null && recommendation.actions.length > 0 ? (
        <InlineStack gap="200" alignBlock="center">
          {recommendation.actions.map((action) =>
            action.kind === 'link' ? (
              <Link
                key={action.label}
                href={action.href}
                target={action.target}
                iconPosition="trailing"
                icon={action.target != null ? 'exit' : undefined}
                onClick={handleActionClick}
              >
                {action.label}
              </Link>
            ) : (
              <Button
                key={action.label}
                size="sm"
                icon={action.icon}
                onClick={() => {
                  handleActionClick();
                  action.onClick();
                }}
              >
                {action.label}
              </Button>
            ),
          )}
        </InlineStack>
      ) : null}
    </Stack>
  );
}

export function RecommendationsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { simulate } = useWorkspaceHealthSimulation();
  const session = useCopilotSessionContext();
  const aiEnabled = useFeatureFlag(FeatureFlag.AiEnabled);
  const aiOptimizeEnabled = useFeatureFlag(FeatureFlag.WorkspaceHealthAiOptimize) && aiEnabled;

  const healthQuery = useEnvHealth(workspaceId, simulate);
  const segmentsQuery = useEnvHealthSegments(workspaceId, simulate);
  const campaignsQuery = useEnvHealthCampaigns(workspaceId, simulate);

  const data = healthQuery.data;
  const segments = segmentsQuery.data?.segments;
  const campaigns = campaignsQuery.data;
  const isLoading = healthQuery.isLoading || segmentsQuery.isLoading || campaignsQuery.isLoading;
  const recommendations =
    data != null ? deriveRecommendations({ data, segments, campaigns, workspaceId, session, aiOptimizeEnabled }) : [];
  const [index, setIndex] = useState(0);

  const currentIndex = Math.min(index, Math.max(recommendations.length - 1, 0));
  const currentRecommendation = recommendations[currentIndex];

  return (
    <Panel>
      <PanelHeader>
        <InlineStack alignBlock="center" gap="050">
          <PanelTitle>Optimizations</PanelTitle>
          {recommendations.length > 1 ? <Badge>{recommendations.length}</Badge> : null}
        </InlineStack>
        {recommendations.length > 1 ? (
          <PanelActions>
            <Button
              variant="subtle"
              size="sm"
              icon="chevron-left"
              isIconOnly
              isDisabled={currentIndex === 0}
              onClick={() => {
                void track('workspaceHealth', 'recommendationCarouselClicked', { Direction: 'prev' });
                setIndex((index_) => Math.max(0, index_ - 1));
              }}
            />
            <Button
              variant="subtle"
              size="sm"
              icon="chevron-right"
              isIconOnly
              isDisabled={currentIndex >= recommendations.length - 1}
              onClick={() => {
                void track('workspaceHealth', 'recommendationCarouselClicked', { Direction: 'next' });
                setIndex((index_) => Math.min(recommendations.length - 1, index_ + 1));
              }}
            />
          </PanelActions>
        ) : null}
      </PanelHeader>
      <PanelSection>
        {isLoading ? (
          <InlineStack gap="100" alignBlock="center">
            <Spinner shouldShowLoadingText label="Analyzing workspace" />
          </InlineStack>
        ) : currentRecommendation == null ? (
          <InlineStack gap="100" alignBlock="center">
            <Icon name="check-circle" size="sm" color="success" />
            <Text size="s" color="subtle">
              No recommendations right now
            </Text>
          </InlineStack>
        ) : (
          <RecommendationContent recommendation={currentRecommendation} />
        )}
      </PanelSection>
    </Panel>
  );
}
